/**
 * Avatar personality presets — the SINGLE source of truth for the five
 * shipped personalities and the knob-resolution logic that turns a
 * sparse `personality` settings record into concrete runtime knobs.
 *
 * Aug 14 2026, "avatar personalities" feature. Design notes:
 *
 *  - The numbers live HERE and only here. The builder settings modal and
 *    the Animation Tester consume this module through the
 *    `@camlin/nextgen-avatar` mirror package; the sparse settings chain
 *    stores only `{ presetId, style, communicationStyle }` (plus any
 *    explicit knob-group overrides), never a copy of the preset numbers.
 *    That way retuning a preset is a one-file change.
 *
 *  - Runtime knobs are resolved from `presetId` and the four explicit
 *    knob groups (`expression` / `gesture` / `idle` / `listening`).
 *    The LEGACY personality fields (`style`, `animationEnergy`,
 *    `expressionIntensity`, `sentimentSensitivity`, `moodIntensity`)
 *    are deliberately NOT re-applied at runtime: the Built-In presets in
 *    `packages/avatar-settings` bake those into the `animations` /
 *    `moods` / `sentiment` sections at definition time via
 *    `behaviourForPersonality`. Re-applying them here would scale the
 *    same numbers twice and change every existing Built-In assignment.
 *
 *  - `communicationStyle` is carried through so hosts / future TTS style
 *    hints can read it from the resolved knobs; nothing in the renderer
 *    consumes it yet.
 */

import type { GestureType } from './gestureAnimation';

export type PersonalityPresetId =
  | 'warm-bubbly'
  | 'calm-professional'
  | 'energetic'
  | 'gentle-reserved'
  | 'playful';

/** Expression bias: mood-overlay scaling + optional resting-face offsets. */
export interface PersonalityExpressionBias {
  /** Global multiplier on mood-overlay intensity (composes with the per-model `moodIntensity` scale). */
  intensityScale?: number;
  /** Per-mood multipliers layered on top of `intensityScale` (keys are mood/sentiment names). */
  perMood?: Record<string, number>;
  /** Additive blendshape offsets applied to the NEUTRAL mood baseline (the resting face). */
  restingFace?: Record<string, number>;
}

/** Gesture policy: allow-list, favoured set, auto-trigger frequency and amplitude. */
export interface PersonalityGesturePolicy {
  /** When set, ONLY these gestures may play (explicit or auto). */
  allowed?: GestureType[];
  /** Favoured gestures bypass the auto-trigger frequency gate. */
  favoured?: GestureType[];
  /**
   * Probability scale for AUTO-triggered gestures (text-detected).
   * Explicitly requested gestures always play. Values > 1 clamp to 1 for
   * gating (we never invent extra triggers — no new trigger path).
   */
  idleFrequencyScale?: number;
  /** Multiplier on gesture skeletal + face intensity. Composes with per-model gating (e.g. Cara's 0.32 shrug). */
  amplitudeScale?: number;
}

/** Idle energy: sway/micro-motion, head liveliness, blink rate. */
export interface PersonalityIdleEnergy {
  /** Multiplier on idle micro-motion intensity (and breathing depth). */
  swayScale?: number;
  /** Multiplier on idle animation speed (head-motion liveliness). */
  headMotionScale?: number;
  /** Multiplier on blink frequency. */
  blinkRateScale?: number;
}

/** Listening style: nod frequency + head tilt amount. */
export interface PersonalityListeningStyle {
  nodFrequencyScale?: number;
  tiltAmountScale?: number;
}

/**
 * The `personality` object as it travels through the unified avatar
 * settings chain (sparse — every field optional). Mirrors the loose zod
 * schema in `@camlin/canonical-kit` `avatarSettingsSchema.personality`.
 */
export interface AvatarPersonalityConfig {
  /** New (Aug 2026): drives runtime knob resolution via PERSONALITY_PRESETS. */
  presetId?: PersonalityPresetId | string;
  /** Legacy style label — kept for the Built-In bake path; NOT runtime-applied. */
  style?: string;
  /** Host-facing communication hint; flows to TTS style hints later. */
  communicationStyle?: string;
  /** Explicit knob groups — override the preset's values per field. */
  expression?: PersonalityExpressionBias;
  gesture?: PersonalityGesturePolicy;
  idle?: PersonalityIdleEnergy;
  listening?: PersonalityListeningStyle;
  /** Legacy bake-time fields (behaviourForPersonality) — tolerated, ignored at runtime. */
  animationEnergy?: number;
  expressionIntensity?: number;
  sentimentSensitivity?: number;
  moodIntensity?: Record<string, number>;
  [key: string]: unknown;
}

/** Fully-defaulted knobs the renderer applies. Every field is concrete. */
export interface ResolvedPersonalityKnobs {
  presetId: PersonalityPresetId | null;
  communicationStyle: string | null;
  expression: {
    intensityScale: number;
    perMood: Record<string, number>;
    restingFace: Record<string, number>;
  };
  gesture: {
    allowed: GestureType[] | null;
    favoured: GestureType[];
    idleFrequencyScale: number;
    amplitudeScale: number;
  };
  idle: {
    swayScale: number;
    headMotionScale: number;
    blinkRateScale: number;
  };
  listening: {
    nodFrequencyScale: number;
    tiltAmountScale: number;
  };
}

export interface PersonalityPresetDefinition {
  id: PersonalityPresetId;
  name: string;
  description: string;
  communicationStyle: string;
  expression: Required<Pick<PersonalityExpressionBias, 'intensityScale'>> & {
    perMood: Record<string, number>;
    restingFace: Record<string, number>;
  };
  gesture: {
    allowed: GestureType[] | null;
    favoured: GestureType[];
    idleFrequencyScale: number;
    amplitudeScale: number;
  };
  idle: Required<PersonalityIdleEnergy>;
  listening: Required<PersonalityListeningStyle>;
}

export const PERSONALITY_PRESETS: PersonalityPresetDefinition[] = [
  {
    id: 'warm-bubbly',
    name: 'Warm & Bubbly',
    description: 'Bright resting smile, lively idle sway, generous nods.',
    communicationStyle: 'Warm, upbeat and encouraging. Celebrates small wins, softens setbacks.',
    expression: {
      intensityScale: 1.1,
      perMood: { positive: 1.15, empathetic: 1.1, negative: 0.85 },
      restingFace: { mouthSmile_L: 0.06, mouthSmile_R: 0.06 },
    },
    gesture: {
      allowed: null,
      favoured: ['nod', 'shrug'],
      idleFrequencyScale: 1.2,
      amplitudeScale: 1.1,
    },
    idle: { swayScale: 1.15, headMotionScale: 1.15, blinkRateScale: 1.1 },
    listening: { nodFrequencyScale: 1.2, tiltAmountScale: 1.1 },
  },
  {
    id: 'calm-professional',
    name: 'Calm Professional',
    description: 'Composed and steady. Restrained gestures, measured expressions.',
    communicationStyle: 'Clear, courteous and concise. Warm without being casual.',
    expression: {
      intensityScale: 0.85,
      perMood: { positive: 0.9, empathetic: 0.95 },
      restingFace: {},
    },
    gesture: {
      allowed: ['nod', 'lean-forward', 'lean-back'],
      favoured: ['nod'],
      idleFrequencyScale: 0.7,
      amplitudeScale: 0.85,
    },
    idle: { swayScale: 0.8, headMotionScale: 0.85, blinkRateScale: 0.95 },
    listening: { nodFrequencyScale: 0.9, tiltAmountScale: 0.8 },
  },
  {
    id: 'energetic',
    name: 'Energetic',
    description: 'High-tempo idle, frequent gestures, big expressions.',
    communicationStyle: 'Upbeat and brisk. Short sentences, plenty of encouragement.',
    expression: {
      intensityScale: 1.2,
      perMood: { positive: 1.25, confused: 1.1 },
      restingFace: { mouthSmile_L: 0.04, mouthSmile_R: 0.04 },
    },
    gesture: {
      allowed: null,
      favoured: ['nod', 'shake', 'shrug'],
      idleFrequencyScale: 1.4,
      amplitudeScale: 1.2,
    },
    idle: { swayScale: 1.3, headMotionScale: 1.25, blinkRateScale: 1.15 },
    listening: { nodFrequencyScale: 1.3, tiltAmountScale: 1.15 },
  },
  {
    id: 'gentle-reserved',
    name: 'Gentle & Reserved',
    description: 'Quiet presence. Muted celebration (0.6×), minimal gesturing.',
    communicationStyle: 'Soft-spoken and unhurried. Gives space, never overwhelms.',
    expression: {
      intensityScale: 0.7,
      perMood: { positive: 0.6, negative: 0.8, empathetic: 0.9 },
      restingFace: {},
    },
    gesture: {
      allowed: ['nod', 'lean-back'],
      favoured: [],
      idleFrequencyScale: 0.5,
      amplitudeScale: 0.7,
    },
    idle: { swayScale: 0.65, headMotionScale: 0.7, blinkRateScale: 0.9 },
    listening: { nodFrequencyScale: 0.7, tiltAmountScale: 0.9 },
  },
  {
    id: 'playful',
    name: 'Playful',
    description: 'Expressive tilts, cheeky shrugs, animated reactions.',
    communicationStyle: 'Light-hearted and curious. Uses humour, keeps things fun.',
    expression: {
      intensityScale: 1.15,
      perMood: { positive: 1.2, confused: 1.15, concerned: 0.9 },
      restingFace: { mouthSmile_L: 0.05, mouthSmile_R: 0.05, browInnerUp: 0.03 },
    },
    gesture: {
      allowed: null,
      favoured: ['shrug', 'nod'],
      idleFrequencyScale: 1.3,
      amplitudeScale: 1.15,
    },
    idle: { swayScale: 1.2, headMotionScale: 1.2, blinkRateScale: 1.05 },
    listening: { nodFrequencyScale: 1.1, tiltAmountScale: 1.25 },
  },
];

export function getPersonalityPreset(id: string | null | undefined): PersonalityPresetDefinition | null {
  if (!id) return null;
  return PERSONALITY_PRESETS.find((preset) => preset.id === id) ?? null;
}

/** Scales are sane in [0, 2]; the final morph influence is clamped to [0, 1] downstream. */
function clampScale(value: unknown, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(2, n));
}

function sanitizeScaleMap(input: Record<string, number> | undefined, base: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = { ...base };
  for (const [key, value] of Object.entries(input ?? {})) {
    const n = Number(value);
    if (Number.isFinite(n)) out[key] = Math.max(0, Math.min(2, n));
  }
  return out;
}

function sanitizeOffsetMap(input: Record<string, number> | undefined): Record<string, number> | null {
  if (!input) return null;
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(input)) {
    const n = Number(value);
    if (Number.isFinite(n)) out[key] = Math.max(-1, Math.min(1, n));
  }
  return out;
}

const KNOWN_GESTURES: GestureType[] = ['nod', 'shake', 'shrug', 'lean-forward', 'lean-back', 'wave'];

function sanitizeGestureList(input: unknown): GestureType[] | null {
  if (!Array.isArray(input)) return null;
  const out = input.filter((g): g is GestureType => KNOWN_GESTURES.includes(g as GestureType));
  return out;
}

/**
 * Resolve a sparse settings-chain `personality` record into concrete
 * runtime knobs. Returns null when there is nothing to apply (no
 * presetId and no explicit knob groups) — legacy-only records
 * (`style` + bake-time multipliers) intentionally resolve to null, see
 * the module docstring.
 */
export function resolvePersonalityKnobs(
  input: AvatarPersonalityConfig | null | undefined,
): ResolvedPersonalityKnobs | null {
  if (!input || typeof input !== 'object') return null;

  const preset = getPersonalityPreset(typeof input.presetId === 'string' ? input.presetId : null);
  const hasExplicitKnobs = Boolean(input.expression || input.gesture || input.idle || input.listening);
  if (!preset && !hasExplicitKnobs) return null;

  const base: ResolvedPersonalityKnobs = preset
    ? {
        presetId: preset.id,
        communicationStyle: preset.communicationStyle,
        expression: {
          intensityScale: preset.expression.intensityScale,
          perMood: { ...preset.expression.perMood },
          restingFace: { ...preset.expression.restingFace },
        },
        gesture: {
          allowed: preset.gesture.allowed ? [...preset.gesture.allowed] : null,
          favoured: [...preset.gesture.favoured],
          idleFrequencyScale: preset.gesture.idleFrequencyScale,
          amplitudeScale: preset.gesture.amplitudeScale,
        },
        idle: { ...preset.idle },
        listening: { ...preset.listening },
      }
    : {
        presetId: null,
        communicationStyle: null,
        expression: { intensityScale: 1, perMood: {}, restingFace: {} },
        gesture: { allowed: null, favoured: [], idleFrequencyScale: 1, amplitudeScale: 1 },
        idle: { swayScale: 1, headMotionScale: 1, blinkRateScale: 1 },
        listening: { nodFrequencyScale: 1, tiltAmountScale: 1 },
      };

  if (typeof input.communicationStyle === 'string' && input.communicationStyle.trim()) {
    base.communicationStyle = input.communicationStyle.trim();
  }

  if (input.expression) {
    base.expression.intensityScale = clampScale(input.expression.intensityScale, base.expression.intensityScale);
    base.expression.perMood = sanitizeScaleMap(input.expression.perMood, base.expression.perMood);
    const resting = sanitizeOffsetMap(input.expression.restingFace);
    if (resting) base.expression.restingFace = resting;
  }

  if (input.gesture) {
    const allowed = sanitizeGestureList(input.gesture.allowed);
    if (allowed) base.gesture.allowed = allowed;
    const favoured = sanitizeGestureList(input.gesture.favoured);
    if (favoured) base.gesture.favoured = favoured;
    base.gesture.idleFrequencyScale = clampScale(input.gesture.idleFrequencyScale, base.gesture.idleFrequencyScale);
    base.gesture.amplitudeScale = clampScale(input.gesture.amplitudeScale, base.gesture.amplitudeScale);
  }

  if (input.idle) {
    base.idle.swayScale = clampScale(input.idle.swayScale, base.idle.swayScale);
    base.idle.headMotionScale = clampScale(input.idle.headMotionScale, base.idle.headMotionScale);
    base.idle.blinkRateScale = clampScale(input.idle.blinkRateScale, base.idle.blinkRateScale);
  }

  if (input.listening) {
    base.listening.nodFrequencyScale = clampScale(input.listening.nodFrequencyScale, base.listening.nodFrequencyScale);
    base.listening.tiltAmountScale = clampScale(input.listening.tiltAmountScale, base.listening.tiltAmountScale);
  }

  return base;
}

/**
 * Structural equality for resolved personality knobs.
 *
 * `replayAvatarRuntimeConfig` dispatches `avatar:personality:configure` on
 * EVERY config replay, and multiple dispatchers (App.tsx, CamlinAvatar,
 * useHostedLiveControllerEffects) each have their own dedup key — so the
 * Avatar handler can receive the same personality many times per call.
 * Re-applying identical knobs restarts blinking/idle and stomps the
 * listening loop mid-call (Aug 16 2026 Visual IVR defect). The handler
 * uses this check to no-op when nothing actually changed.
 *
 * JSON comparison is safe here: both sides come from
 * `resolvePersonalityKnobs`, which constructs the object with a fixed
 * key order.
 */
export function personalityKnobsEqual(
  a: ResolvedPersonalityKnobs | null,
  b: ResolvedPersonalityKnobs | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/** The animation-config shape Avatar.tsx keeps in `animationConfigRef`. */
export interface PersonalityAnimationConfig {
  blink: { enabled: boolean; frequency: number; duration: number };
  breathing: { enabled: boolean; rate: number; intensity: number };
  idle: { enabled: boolean; intensity: number; speed: number };
  listening: { enabled: boolean; nodFrequency: number; tiltAmount: number };
  thinking: { enabled: boolean; eyeSearchIntensity: number };
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * Apply personality idle/blink/listening scales to a BASE animation
 * config (the editor-supplied absolute values), producing the effective
 * config the animation loops read. Pure and idempotent: always derive
 * from the base, never from a previously-scaled config, so re-applying
 * a personality can never compound.
 */
export function applyPersonalityToAnimationConfig(
  base: PersonalityAnimationConfig,
  knobs: ResolvedPersonalityKnobs | null,
): PersonalityAnimationConfig {
  const cloned: PersonalityAnimationConfig = {
    blink: { ...base.blink },
    breathing: { ...base.breathing },
    idle: { ...base.idle },
    listening: { ...base.listening },
    thinking: { ...base.thinking },
  };
  if (!knobs) return cloned;

  cloned.blink.frequency = Math.max(1, Math.round(base.blink.frequency * knobs.idle.blinkRateScale));
  cloned.breathing.intensity = clamp01(base.breathing.intensity * knobs.idle.swayScale);
  cloned.idle.intensity = clamp01(base.idle.intensity * knobs.idle.swayScale);
  cloned.idle.speed = clamp01(base.idle.speed * knobs.idle.headMotionScale);
  cloned.listening.nodFrequency = Math.max(1, Math.round(base.listening.nodFrequency * knobs.listening.nodFrequencyScale));
  // 0.2 tilt cap matches behaviourForPersonality in packages/avatar-settings.
  cloned.listening.tiltAmount = Math.min(0.2, base.listening.tiltAmount * knobs.listening.tiltAmountScale);
  return cloned;
}
